// src/pages/Drive.jsx
import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  useMediaQuery,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { ClipboardCheck, PlusCircle, MapPin, RefreshCw, Timer, Euro } from "lucide-react";

import { useDelivery } from "../../context/DeliveryContext";
import { useUser } from "../../context/UserContext";
import { useAuth } from "../../context/AuthContext";
import { updateUserProfile } from "../../firebase/user";
import { useTheme } from "../../context/ThemeContext";
import { DeliveryList } from "./DeliveryList/index";
import { DeliveryDetail } from "./DeliveryDetail";
import { AdminModal } from "./AdminModal";
import { ActiveBar, AREA_PRESETS, TRANSPORT_PRESETS } from "./ActiveBar";


export function Drive() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [adminModalOpen, setAdminModalOpen] = React.useState(false);
  const [adminModalTab, setAdminModalTab]   = React.useState("create");

  const [sort, setSort]                           = React.useState("urgency");
  const [locationModalOpen, setLocationModalOpen] = React.useState(false);
  const [courierLocation, setCourierLocation]     = React.useState(null);

  // Add alongside alueParam
const kuljetusParam = searchParams.get("kuljetustapa")?.toLowerCase() ?? null;
const transportFilter = kuljetusParam ? (TRANSPORT_PRESETS[kuljetusParam] ?? kuljetusParam) : null;

  const deliveryId = searchParams.get("deliveryId");
  const tabParam   = searchParams.get("tab");
  const alueParam  = searchParams.get("alue")?.toLowerCase() ?? null;

  const { loading, error } = useDelivery();
  const { userData }       = useUser();
  const { user }           = useAuth();
  const { colors }         = useTheme();

  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const isSpecialCourier =
    Array.isArray(userData?.roles) && userData.roles.includes("specialCourier");

  const isShadowBanned = Boolean(userData?.shadowBan);

  // Seed courier location — URL param takes priority over userData
  const locationSeeded = React.useRef(false);
  React.useEffect(() => {
    if (locationSeeded.current) return;

    // 1. Known preset from URL
    if (alueParam && AREA_PRESETS[alueParam]) {
      setCourierLocation(AREA_PRESETS[alueParam]);
      locationSeeded.current = true;
      return;
    }

    // 2. Unknown alue param — no coordinates available, mark seeded and move on
    if (alueParam) {
      locationSeeded.current = true;
      return;
    }

    // 3. Saved userData location
    if (userData?.location) {
      const loc = userData.location;
      setCourierLocation({
        coordinates: {
          lat:  loc.coordinates?.lat  ?? null,
          long: loc.coordinates?.long ?? loc.coordinates?.lng ?? null,
        },
        city: loc.city || null,
      });
      locationSeeded.current = true;
    }
  }, [userData, alueParam]);

  // Display label: preset city > capitalized raw param > "Suomi"
  const areaLabel = courierLocation?.city
    || (alueParam ? alueParam.charAt(0).toUpperCase() + alueParam.slice(1) : "Suomi");

  const tabs = useMemo(
    () => [
      { label: "Aktiiviset",       value: "active"    },
      { label: "Omat kuljetukset", value: "own"       },
      { label: "Valmiit",          value: "completed" },
    ],
    []
  );

  const activeTabValue = tabs.some((t) => t.value === tabParam) ? tabParam : "active";
  const activeTabIndex = tabs.findIndex((t) => t.value === activeTabValue);
  const showActiveBar  = activeTabValue === "active";

  const openAdmin = (tab) => { setAdminModalTab(tab); setAdminModalOpen(true); };

  const bottomBarRows = showActiveBar && isSpecialCourier ? 2 : 1;
  const bottomBarPb   = isMobile
    ? bottomBarRows === 2 ? "116px" : "68px"
    : bottomBarRows === 2 ? "96px"  : "56px";

  if (deliveryId) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
        <DeliveryDetail deliveryId={deliveryId} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
      <Box sx={{ height: { xs: 64, sm: 72 } }} />

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            border: `1px solid ${colors.semantic?.error?.border || "transparent"}`,
            bgcolor: colors.semantic?.error?.background,
          }}
        >
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Box
        sx={{
          mb: 2,
          p: 0.5,
          borderRadius: 2,
          bgcolor: colors.surface?.raised || "background.paper",
          border: `1px solid ${colors.surface?.border || "rgba(0,0,0,0.12)"}`,
          overflow: "hidden",
        }}
      >
        <Tabs
          value={activeTabIndex}
          onChange={(_, index) =>
            setSearchParams(
              (prev) => {
                const p = new URLSearchParams(prev);
                p.set("tab", tabs[index].value);
                p.delete("deliveryId");
                p.delete("view");
                return p;
              },
              { replace: false }
            )
          }
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile
          aria-label="delivery tabs"
          sx={{
            minHeight: 44,
            "& .MuiTab-root": { minHeight: 44, textTransform: "none", fontWeight: 600, px: 2 },
          }}
        >
          {tabs.map((t) => <Tab key={t.value} label={t.label} />)}
        </Tabs>
      </Box>

      {/* Content */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ pb: bottomBarPb }}>
          <DeliveryList
            status={activeTabValue}
            sort={sort}
            courierLocation={courierLocation}
            areaFilter={alueParam}
            transportFilter={transportFilter}
            locationModalOpen={locationModalOpen}
            onLocationModalClose={() => setLocationModalOpen(false)}
            shadowBan={isShadowBanned}
            onLocationConfirm={async (loc) => {
              setCourierLocation(loc);
              locationSeeded.current = true;

              setSearchParams((prev) => {
                const p = new URLSearchParams(prev);
                const matchedPreset = Object.entries(AREA_PRESETS).find(
                  ([, v]) => v.city?.toLowerCase() === loc.city?.toLowerCase()
                );
                if (matchedPreset) p.set("alue", matchedPreset[0]);
                else p.delete("alue");
                return p;
              }, { replace: true });

              if (user?.uid) {
                try {
                  await updateUserProfile(user.uid, {
                    location: {
                      coordinates: loc.coordinates,
                      city: loc.city || "",
                      address: loc.address || "",
                    },
                  });
                } catch (err) {
                  console.error("Failed to save location:", err);
                }
              }
            }}
          />
        </Box>
      )}

      {/* ── Sticky bottom bar ─────────────────────────────────────────── */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          bgcolor: colors.surface?.raised || "background.paper",
          borderTop: `1px solid ${colors.surface?.border || "rgba(0,0,0,0.12)"}`,
          boxShadow: "0 -2px 12px rgba(0,0,0,0.08)",
        }}
      >
        {/* Row 1 (active tab only): area + sort */}
        {showActiveBar && (
  <ActiveBar
    areaLabel={areaLabel}
    alueParam={alueParam}
    transportFilter={transportFilter}
    sort={sort}
    isSpecialCourier={isSpecialCourier}
    colors={colors}
    onSetSearchParams={(fn) => setSearchParams(fn, { replace: true })}
    onSetCourierLocation={(loc) => { setCourierLocation(loc); locationSeeded.current = true; }}
    onSortChange={setSort}
  />
)}
        {/* Row 2 (admin only) */}
        {isSpecialCourier && (
          <Box
            sx={{
              px: { xs: 2, sm: 4 },
              py: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{ mr: 0.5, display: { xs: "none", sm: "block" }, letterSpacing: 1, textTransform: "uppercase" }}
            >
              Admin
            </Typography>
            <Button
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              startIcon={<ClipboardCheck size={15} />}
              onClick={() => openAdmin("deliveries")}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Kuljetukset
            </Button>
            <Button
              variant="contained"
              disableElevation
              size={isMobile ? "small" : "medium"}
              startIcon={<PlusCircle size={15} />}
              onClick={() => openAdmin("create")}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Luo kuljetus
            </Button>
          </Box>
        )}
      </Box>

      {adminModalOpen && (
        <AdminModal
          open={adminModalOpen}
          onClose={() => setAdminModalOpen(false)}
          initialTab={adminModalTab}
        />
      )}
    </Container>
  );
}